import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import i18n from "../i18n";

// Supported languages — kept as a named export for backwards compatibility
// with existing callers (LanguageSwitcher, LoginPage, ProfilePage, SettingsPage)
// that import { LANGUAGES } from this module.
export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧", flagImg: "https://flagcdn.com/w40/gb.png" },
  { code: "pt", name: "Português (PT)", flag: "🇵🇹", flagImg: "https://flagcdn.com/w40/pt.png" },
  { code: "es", name: "Español", flag: "🇪🇸", flagImg: "https://flagcdn.com/w40/es.png" },
];

const SUPPORTED = LANGUAGES.map((l) => l.code);
const STORAGE_KEY = "trainedbyrs_language";
// Records that the user picked a language THEMSELVES (rather than it being
// restored from their account). Session-scoped on purpose: it exists to carry a
// pre-login choice through the login redirect, after which the account
// preference takes over again. See LanguageSyncer.
export const EXPLICIT_KEY = "trainedbyrs_language_explicit";
// Same fallback as api/client.js. Without it, an unset REACT_APP_API_URL made
// this module silently skip the account sync entirely — the language changed on
// screen but was never saved, with no error anywhere.
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  // compat fields
  currentLanguage: "en",
  changeLanguage: () => {},
  languages: LANGUAGES,
});

function normalize(lang) {
  if (!lang) return "en";
  const k = String(lang).toLowerCase().split("-")[0];
  return SUPPORTED.includes(k) ? k : "en";
}

export function LanguageProvider({ children, initialLanguage }) {
  const [language, setLanguageState] = useState(() =>
    normalize(
      initialLanguage ||
        (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) ||
        i18n.language ||
        "en"
    )
  );

  // Keep axios + i18n in sync with the context on mount + on changes
  useEffect(() => {
    if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
      axios.defaults.headers.common["Accept-Language"] = language;
    }
    if (i18n.language !== language) {
      try {
        i18n.changeLanguage(language);
      } catch (_) {}
    }
  }, [language]);

  // opts.explicit === false marks a programmatic restore (LanguageSyncer
  // applying the account preference); anything else is a deliberate user pick.
  const setLanguage = useCallback((lang, opts = {}) => {
    const next = normalize(lang);
    setLanguageState(next);
    try {
      i18n.changeLanguage(next);
    } catch (_) {}
    try {
      localStorage.setItem(STORAGE_KEY, next);
      if (opts.explicit !== false) sessionStorage.setItem(EXPLICIT_KEY, next);
    } catch (_) {}
    if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
      axios.defaults.headers.common["Accept-Language"] = next;
    }
    // Fire-and-forget DB sync. Swallow errors — UI shouldn't block on this.
    if (API_BASE) {
      try {
        const p = axios.patch(`${API_BASE}/users/me`, { language: next });
        if (p && typeof p.catch === "function") {
          p.catch(() => {});
        }
      } catch (_) {}
    }
  }, []);

  // Backwards-compat alias used by existing callers
  const changeLanguage = useCallback(
    async (langCode) => {
      setLanguage(langCode);
    },
    [setLanguage]
  );

  const value = useMemo(
    () => ({
      // new API
      language,
      setLanguage,
      // legacy API (LanguageSwitcher, LoginPage, ProfilePage, SettingsPage)
      currentLanguage: language,
      changeLanguage,
      languages: LANGUAGES,
    }),
    [language, setLanguage, changeLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageContext;
